import React from "react";
import { graphql } from "babel-plugin-relay/macro";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import moment from "moment-timezone";
import { TreeConfigurationEditDialogProps } from "../../components/Config.interfaces";
import { useFragment, useMutation } from "react-relay";
import { RenderConfig, ValidatedField } from "../../../../../../core/form/ValidatedField";
import { NotAfterDateTimeRewardConfigForm_TreeNodeFragment$key } from "../../../../../../../../__generated__/NotAfterDateTimeRewardConfigForm_TreeNodeFragment.graphql";
import { NotAfterDateTimeRewardConfigForm_EditMutation } from "../../../../../../../../__generated__/NotAfterDateTimeRewardConfigForm_EditMutation.graphql";
import { NotAfterDateTimeRewardConfigForm_AfterDateTimeDoNotRewardTreeConfigFragment$key } from "../../../../../../../../__generated__/NotAfterDateTimeRewardConfigForm_AfterDateTimeDoNotRewardTreeConfigFragment.graphql";
import { ConfigDatePicker } from "../../components/ConfigDatePicker";
import { CONFIGS_TRANSLATIONS } from "../../../../../../../i18n/config/i18n";

const CONFIGURATION_FRAGMENT = graphql`
	fragment NotAfterDateTimeRewardConfigForm_AfterDateTimeDoNotRewardTreeConfigFragment on AfterDateTimeDoNotRewardTreeConfig {
		id
		configType
		dateTime
	}
`;

const TREE_NODE_FRAGMENT = graphql`
	fragment NotAfterDateTimeRewardConfigForm_TreeNodeFragment on TreeNode {
		id
	}
`;

const EDIT_CONFIGURATION_MUTATION = graphql`
	mutation NotAfterDateTimeRewardConfigForm_EditMutation(
		$input: EditAfterDateTimeDoNotRewardTreeConfigInput!
	) {
		Admin {
			Tree {
				editAfterDateTimeDoNotRewardTreeConfig(input: $input) {
					rootNode {
						...RewardTreeConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface FormState {
	date: string;
}

interface Props extends TreeConfigurationEditDialogProps {
	configurationFragmentRef: NotAfterDateTimeRewardConfigForm_AfterDateTimeDoNotRewardTreeConfigFragment$key;
	treeNodeFragmentRef: NotAfterDateTimeRewardConfigForm_TreeNodeFragment$key;
	onBack?: () => void;
}

export const NotAfterDateTimeRewardConfigForm = ({
	configurationFragmentRef,
	treeNodeFragmentRef,
	onBack,
}: Props) => {
	const treeNode = useFragment<NotAfterDateTimeRewardConfigForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const configuration =
		useFragment<NotAfterDateTimeRewardConfigForm_AfterDateTimeDoNotRewardTreeConfigFragment$key>(
			CONFIGURATION_FRAGMENT,
			configurationFragmentRef,
		);
	const [edit, isEditing] = useMutation<NotAfterDateTimeRewardConfigForm_EditMutation>(
		EDIT_CONFIGURATION_MUTATION,
	);

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			date: configuration.dateTime || moment().add(1, "year").format(),
		},
		onSubmit: (values, { setSubmitting }) => {
			edit({
				variables: {
					input: {
						rootNodeId: treeNode.id,
						configId: configuration.id,
						newDateTime: values.date,
					},
				},
			});
			setSubmitting(false);
		},
	});

	return (
		<>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, string>
					name={"date"}
					label={CONFIGS_TRANSLATIONS[configuration.configType]}
					className="mb-4"
					formikConfig={formik}
					component={({
						fieldName,
						fieldValue,
						updateField,
						disabled,
					}: RenderConfig<string>) => {
						return (
							<ConfigDatePicker
								name={fieldName}
								value={fieldValue}
								onUpdate={updateField}
								disabled={disabled}
							/>
						);
					}}
				/>

				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditing}
					type="submit"
					label="Speichern"
					className="mt-2"
				/>

				<Button
					type="button"
					onClick={() => {
						onBack!();
					}}
					label="Zurück"
					className="p-button-secondary mt-2"
				/>
			</form>
		</>
	);
};
